import React, { useMemo } from 'react';
import { Dialog } from '../reusable';
import { getActiveQuotationLossReasonOptions } from './commercialCatalogDefaults';
import {
  buildLossCompetitorLineMap,
  calcAggregateVariationPct,
  calcCompetitorLineSubtotal,
  calcOurLineSubtotal,
  calcOfferTotals,
  calcPriceVariationPct,
  formatLossCompetitorMoney,
  formatVariationPct,
  lossCompetitorHasData,
  parseLossCompetitor,
} from './quotationLossCompetitorUtils';

const LICITATION_STATUS_STYLES = {
  Abierta: { bg: '#dbeafe', text: '#1d4ed8' },
  'En Analisis': { bg: '#fef3c7', text: '#92400e' },
  'Oferta Enviada': { bg: '#e0e7ff', text: '#3730a3' },
  Adjudicada: { bg: '#dcfce7', text: '#166534' },
  Perdida: { bg: '#fee2e2', text: '#b91c1c' },
  Cancelada: { bg: '#e5e7eb', text: '#374151' },
};

const theme = (isDarkMode) =>
  isDarkMode
    ? {
        pageBg: '#18181b',
        cardBg: '#27272a',
        cardBorder: '#3f3f46',
        tableHead: '#1f1f23',
        text: '#f4f4f5',
        muted: '#a1a1aa',
        sectionBg: '#1f1f23',
        ourBg: 'rgba(37, 99, 235, 0.08)',
        competitorBg: 'rgba(220, 38, 38, 0.08)',
      }
    : {
        pageBg: '#eef2f7',
        cardBg: '#ffffff',
        cardBorder: '#d8dee8',
        tableHead: '#f1f5f9',
        text: '#0f172a',
        muted: '#64748b',
        sectionBg: '#f8fafc',
        ourBg: '#eff6ff',
        competitorBg: '#fef2f2',
      };

const formatMoney = (value, currency = 'CLP') => {
  const raw = String(currency || 'CLP').trim().toUpperCase() || 'CLP';
  const code = /^[A-Z]{3}$/.test(raw) ? raw : 'CLP';
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: code === 'CLP' ? 0 : 2,
    }).format(Number(value || 0));
  } catch {
    return `${code} ${Number(value || 0).toLocaleString('es-CL')}`;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const Card = ({ t, children, style }) => (
  <div
    style={{
      marginBottom: 12,
      borderRadius: 10,
      border: `1px solid ${t.cardBorder}`,
      background: t.cardBg,
      padding: '14px 16px',
      ...style,
    }}
  >
    {children}
  </div>
);

const Field = ({ label, value, t, emphasize = false }) => (
  <div style={{ minWidth: 0, lineHeight: 1.2 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, marginBottom: 3 }}>{label}</div>
    <div
      style={{
        fontSize: emphasize ? 14 : 13,
        fontWeight: emphasize ? 700 : 600,
        color: t.text,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {value || '—'}
    </div>
  </div>
);

const SectionTitle = ({ children, t }) => (
  <div style={{ margin: '4px 2px 8px', fontSize: 14, fontWeight: 700, color: t.text }}>{children}</div>
);

const resolveLossReasonLabel = (lossReason, options) => {
  const code = String(lossReason || '').trim();
  if (!code) return '';
  const match = (options || []).find(
    (opt) => opt.value === code || opt.code === code || opt.label === code
  );
  return match?.label || code;
};

const thStyle = (t, align = 'left', extra = {}) => ({
  padding: '8px 10px',
  textAlign: align,
  fontSize: 11,
  fontWeight: 600,
  color: t.muted,
  borderBottom: `1px solid ${t.cardBorder}`,
  whiteSpace: 'nowrap',
  ...extra,
});

const tdStyle = (t, align = 'left', extra = {}) => ({
  padding: '8px 10px',
  fontSize: 12,
  textAlign: align,
  borderBottom: `1px solid ${t.cardBorder}`,
  color: t.text,
  ...extra,
});

const PriceComparisonTable = ({ quotation, items, licitation, t }) => {
  const lossReasonOptions = useMemo(() => getActiveQuotationLossReasonOptions(), []);
  const lossReasonLabel = resolveLossReasonLabel(quotation?.loss_reason, lossReasonOptions);
  const competitor = parseLossCompetitor(quotation?.loss_competitor || quotation?.loss_competitor_json);
  const lineMap = buildLossCompetitorLineMap(competitor.line_items);
  const quotationItems = (items || []).filter(
    (item) => Number(item.quotation_id) === Number(quotation.id)
  );
  const currency = quotation?.currency || licitation?.currency || 'CLP';
  const hasCompetitor = lossCompetitorHasData(competitor);
  const competitorName =
    competitor.competitor_name || licitation?.winner || 'Competidor ganador';
  const competitorTotal =
    Number(competitor.competitor_amount || 0) > 0
      ? Number(competitor.competitor_amount)
      : Number(licitation?.winning_amount || 0);
  const totals = calcOfferTotals(quotationItems, lineMap);
  const ourTotal =
    totals.ourTotal > 0
      ? totals.ourTotal
      : quotationItems.reduce((sum, item) => sum + calcOurLineSubtotal(item), 0);
  const aggregateVariation = calcAggregateVariationPct(quotationItems, lineMap);

  if (quotationItems.length === 0) {
    return (
      <Card t={t}>
        <div style={{ fontSize: 13, color: t.muted }}>
          Sin ítems en la cotización {quotation.quotation_code || ''}.
        </div>
      </Card>
    );
  }

  return (
    <Card t={t} style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px 10px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 8 }}>
          Cotización {quotation.quotation_code || `#${quotation.id}`}
          {quotation.status ? ` · ${quotation.status}` : ''}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '10px 14px',
          }}
        >
          {lossReasonLabel ? <Field label="Motivo de pérdida" value={lossReasonLabel} t={t} /> : null}
          <Field label="Competidor ganador" value={competitorName} t={t} emphasize />
          {competitorTotal > 0 ? (
            <Field
              label="Monto total competidor"
              value={formatLossCompetitorMoney(competitorTotal, currency)}
              t={t}
              emphasize
            />
          ) : null}
          {ourTotal > 0 ? (
            <Field
              label="Monto total nuestra oferta"
              value={formatLossCompetitorMoney(ourTotal, currency)}
              t={t}
            />
          ) : null}
        </div>
        {quotation?.loss_buyer_comment ? (
          <div style={{ marginTop: 10 }}>
            <Field label="Comentario del comprador" value={quotation.loss_buyer_comment} t={t} />
          </div>
        ) : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1040 }}>
          <thead>
            <tr style={{ background: t.tableHead }}>
              <th style={thStyle(t, 'right', { width: 36 })}>#</th>
              <th style={thStyle(t, 'left')}>Producto</th>
              <th style={thStyle(t, 'right')}>Cant.</th>
              <th
                colSpan={2}
                style={thStyle(t, 'center', { background: t.ourBg, borderLeft: `2px solid ${t.cardBorder}` })}
              >
                Nuestra cotización
              </th>
              <th
                colSpan={2}
                style={thStyle(t, 'center', { background: t.competitorBg, borderLeft: `2px solid ${t.cardBorder}` })}
              >
                {competitorName}
              </th>
              <th style={thStyle(t, 'right', { borderLeft: `2px solid ${t.cardBorder}` })}>Var. %</th>
            </tr>
            <tr style={{ background: t.tableHead }}>
              <th style={thStyle(t)} />
              <th style={thStyle(t)} />
              <th style={thStyle(t)} />
              <th style={thStyle(t, 'right', { background: t.ourBg, borderLeft: `2px solid ${t.cardBorder}` })}>
                Precio unit.
              </th>
              <th style={thStyle(t, 'right', { background: t.ourBg })}>Subtotal</th>
              <th style={thStyle(t, 'left', { background: t.competitorBg, borderLeft: `2px solid ${t.cardBorder}` })}>
                Producto
              </th>
              <th style={thStyle(t, 'right', { background: t.competitorBg })}>Precio unit.</th>
              <th style={thStyle(t, 'right', { background: t.competitorBg })}>Subtotal</th>
              <th style={thStyle(t, 'right', { borderLeft: `2px solid ${t.cardBorder}` })} />
            </tr>
          </thead>
          <tbody>
            {quotationItems.map((item, index) => {
              const row = lineMap[Number(item.id)] || {};
              const qty = Number(item.quantity || 0);
              const ourUnit = Number(item.unit_price || 0);
              const ourSubtotal = calcOurLineSubtotal(item);
              const competitorUnit = Number(row.unitPrice || 0);
              const competitorSubtotal = calcCompetitorLineSubtotal(qty, competitorUnit);
              const variation = calcPriceVariationPct(ourUnit, competitorUnit);
              const productLabel = item.item_name || item.product_name || item.item_code || '—';
              const colorMeta = [item.color, item.color_code].filter(Boolean).join(' · ');

              return (
                <tr key={item.id || `${item.quotation_id}-${index}`}>
                  <td style={tdStyle(t, 'right', { color: t.muted })}>{index + 1}</td>
                  <td style={tdStyle(t, 'left')}>
                    <div style={{ fontWeight: 600 }}>{productLabel}</div>
                    {colorMeta ? (
                      <div style={{ fontSize: 11, color: t.muted }}>{colorMeta}</div>
                    ) : null}
                  </td>
                  <td style={tdStyle(t, 'right')}>{qty.toLocaleString('es-CL')}</td>
                  <td
                    style={tdStyle(t, 'right', {
                      background: t.ourBg,
                      borderLeft: `2px solid ${t.cardBorder}`,
                      whiteSpace: 'nowrap',
                    })}
                  >
                    {ourUnit > 0 ? formatLossCompetitorMoney(ourUnit, currency) : '—'}
                  </td>
                  <td style={tdStyle(t, 'right', { background: t.ourBg, fontWeight: 600, whiteSpace: 'nowrap' })}>
                    {ourSubtotal > 0 ? formatLossCompetitorMoney(ourSubtotal, currency) : '—'}
                  </td>
                  <td
                    style={tdStyle(t, 'left', {
                      background: t.competitorBg,
                      borderLeft: `2px solid ${t.cardBorder}`,
                      color: t.muted,
                    })}
                  >
                    {row.product || (hasCompetitor ? '—' : '')}
                  </td>
                  <td
                    style={tdStyle(t, 'right', {
                      background: t.competitorBg,
                      whiteSpace: 'nowrap',
                    })}
                  >
                    {competitorUnit > 0 ? formatLossCompetitorMoney(competitorUnit, currency) : '—'}
                  </td>
                  <td
                    style={tdStyle(t, 'right', {
                      background: t.competitorBg,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    })}
                  >
                    {competitorUnit > 0 ? formatLossCompetitorMoney(competitorSubtotal, currency) : '—'}
                  </td>
                  <td
                    style={tdStyle(t, 'right', {
                      borderLeft: `2px solid ${t.cardBorder}`,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    })}
                  >
                    {competitorUnit > 0 ? formatVariationPct(variation) : '—'}
                  </td>
                </tr>
              );
            })}
            <tr style={{ background: t.sectionBg }}>
              <td colSpan={3} style={tdStyle(t, 'left', { fontWeight: 700, borderTop: `2px solid ${t.cardBorder}` })}>
                Totales
              </td>
              <td
                style={tdStyle(t, 'right', {
                  background: t.ourBg,
                  borderLeft: `2px solid ${t.cardBorder}`,
                  borderTop: `2px solid ${t.cardBorder}`,
                })}
              />
              <td
                style={tdStyle(t, 'right', {
                  background: t.ourBg,
                  fontWeight: 800,
                  borderTop: `2px solid ${t.cardBorder}`,
                  whiteSpace: 'nowrap',
                })}
              >
                {formatLossCompetitorMoney(ourTotal, currency)}
              </td>
              <td
                style={tdStyle(t, 'left', {
                  background: t.competitorBg,
                  borderLeft: `2px solid ${t.cardBorder}`,
                  borderTop: `2px solid ${t.cardBorder}`,
                  fontWeight: 600,
                  color: t.muted,
                })}
              >
                Total ofertado
              </td>
              <td
                style={tdStyle(t, 'right', {
                  background: t.competitorBg,
                  borderTop: `2px solid ${t.cardBorder}`,
                })}
              />
              <td
                style={tdStyle(t, 'right', {
                  background: t.competitorBg,
                  fontWeight: 800,
                  borderTop: `2px solid ${t.cardBorder}`,
                  whiteSpace: 'nowrap',
                })}
              >
                {totals.competitorTotal > 0
                  ? formatLossCompetitorMoney(totals.competitorTotal, currency)
                  : competitorTotal > 0
                    ? formatLossCompetitorMoney(competitorTotal, currency)
                    : '—'}
              </td>
              <td
                style={tdStyle(t, 'right', {
                  borderLeft: `2px solid ${t.cardBorder}`,
                  borderTop: `2px solid ${t.cardBorder}`,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                })}
              >
                {formatVariationPct(aggregateVariation)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const LicitationDetailModal = ({
  isOpen,
  onClose,
  licitation,
  quotations = [],
  items = [],
  loading = false,
  isDarkMode = false,
  onEdit,
}) => {
  const linkedItems = Array.isArray(items) ? items : [];
  const comparisonQuotations = useMemo(() => {
    const linkedQuotations = Array.isArray(quotations) ? quotations : [];
    const lost = linkedQuotations.filter(
      (q) => String(q.status || '').trim().toLowerCase() === 'perdida'
    );
    if (lost.length > 0) return lost;
    return linkedQuotations;
  }, [quotations]);

  if (!licitation) return null;

  const t = theme(isDarkMode);
  const status = String(licitation.status || 'Abierta').trim() || 'Abierta';
  const palette = LICITATION_STATUS_STYLES[status] || LICITATION_STATUS_STYLES.Abierta;
  const currency = String(licitation.currency || 'CLP').toUpperCase();

  const generalFields = [
    { label: 'Organización', value: licitation.organization_name, emphasize: true },
    { label: 'Subunidad', value: licitation.organization_unit_name },
    { label: 'Canal', value: licitation.channel },
    { label: 'Responsable', value: licitation.assigned_user },
    { label: 'Código Mercado Público', value: licitation.tender_code },
    { label: 'Fecha cierre', value: formatDate(licitation.closing_date) },
    { label: 'Fecha adjudicación', value: formatDate(licitation.adjudication_date) },
    { label: 'Moneda', value: currency },
    { label: 'Monto estimado', value: formatMoney(licitation.estimated_amount, currency) },
    { label: 'Monto máximo', value: formatMoney(licitation.maximum_amount, currency) },
    { label: 'Ganador', value: licitation.winner, emphasize: true },
    {
      label: 'Monto ganador',
      value:
        licitation.winning_amount == null || licitation.winning_amount === ''
          ? ''
          : formatMoney(licitation.winning_amount, currency),
      emphasize: true,
    },
  ];

  const body = (
    <div
      style={{
        padding: '4px 4px 12px',
        overflowY: 'auto',
        maxHeight: 'calc(90vh - 140px)',
        background: t.pageBg,
      }}
    >
      <Card t={t}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, color: t.muted }}>
              {licitation.licitation_code || '—'}
              {licitation.tender_code ? ` · ${licitation.tender_code}` : ''}
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 18,
                fontWeight: 700,
                color: t.text,
                wordBreak: 'break-word',
              }}
            >
              {licitation.tender_name || 'Licitación sin nombre'}
            </div>
          </div>
          <span
            style={{
              flexShrink: 0,
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              background: palette.bg,
              color: palette.text,
            }}
          >
            {status}
          </span>
        </div>
      </Card>

      <Card t={t}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '12px 20px',
          }}
        >
          {generalFields.map((field) => (
            <Field
              key={field.label}
              label={field.label}
              value={field.value}
              t={t}
              emphasize={field.emphasize}
            />
          ))}
        </div>
      </Card>

      <SectionTitle t={t}>
        Comparativa de precios · nuestra cotización vs competidor
      </SectionTitle>
      {loading ? (
        <Card t={t}>
          <p style={{ margin: 0, fontSize: 13, color: t.muted }}>Cargando ítems y análisis…</p>
        </Card>
      ) : comparisonQuotations.length === 0 ? (
        <Card t={t}>
          <p style={{ margin: 0, fontSize: 13, color: t.muted }}>
            Sin cotizaciones vinculadas para comparar precios.
          </p>
        </Card>
      ) : (
        comparisonQuotations.map((quotation) => (
          <PriceComparisonTable
            key={quotation.id}
            quotation={quotation}
            items={linkedItems}
            licitation={licitation}
            t={t}
          />
        ))
      )}

      {licitation.notes ? (
        <>
          <SectionTitle t={t}>Notas</SectionTitle>
          <Card t={t}>
            <div
              style={{
                fontSize: 13,
                color: t.text,
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {licitation.notes}
            </div>
          </Card>
        </>
      ) : null}

      {onEdit ? (
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => onEdit(licitation)}
            style={{
              height: 40,
              padding: '0 18px',
              borderRadius: 8,
              border: 'none',
              background: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Editar licitación
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Ficha de licitación"
      subtitle="Comparativa de precios y análisis de la oportunidad"
      isDarkMode={isDarkMode}
      size="xxl"
    >
      {body}
    </Dialog>
  );
};

export default LicitationDetailModal;
